import * as RD from '@radix-ui/react-dropdown-menu';
import { type ReactNode, forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

/*
  DropdownMenu — обёртка над Radix DropdownMenu со стилем платформы: профиль-меню,
  контекстные меню карточек, выбор фильтров. Слой z-popover — выше модалок (R12).
*/

export const DropdownMenu = RD.Root;
export const DropdownMenuTrigger = RD.Trigger;
export const DropdownMenuGroup = RD.Group;
export const DropdownMenuPortal = RD.Portal;
export const DropdownMenuSub = RD.Sub;
export const DropdownMenuRadioGroup = RD.RadioGroup;

interface DropdownMenuContentProps extends ComponentPropsWithoutRef<typeof RD.Content> {
  /**
   * rail — «тёмный остров» у главного меню (окно аккаунта): внутри окна вся семантика тёмной темы в
   * обеих темах, поэтому окно читается продолжением меню и не перекрашивается под курсором при смене
   * темы из него самого.
   */
  tone?: 'default' | 'rail';
}

export const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, sideOffset = 6, tone = 'default', ...rest }, ref) => (
    <RD.Portal>
      <RD.Content
        ref={ref}
        sideOffset={sideOffset}
        data-theme={tone === 'rail' ? 'dark' : undefined}
        data-tone={tone === 'rail' ? 'rail' : undefined}
        data-slot="dropdown-content"
        className={cn(
          'z-popover min-w-48 overflow-hidden rounded-md border border-line bg-elevated p-1 shadow-lg',
          // Пружинистый выезд со стороны триггера + ступенчатые пункты: motion.css (.dropdown-anim).
          'dropdown-anim',
          className,
        )}
        {...rest}
      />
    </RD.Portal>
  ),
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

interface DropdownMenuItemProps extends ComponentPropsWithoutRef<typeof RD.Item> {
  leftIcon?: ReactNode;
  destructive?: boolean;
  shortcut?: string;
}

/*
  `asChild` намеренно не передаётся в RD.Item: внутри несколько дочерних элементов
  (иконка + текст + шорткат), а Radix Slot ждёт ровно один — иначе белый экран по клику.
  Для навигации использовать onSelect + useNavigate.
*/
export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, leftIcon, destructive, shortcut, children, asChild: _ignore, ...rest }, ref) => (
    <RD.Item
      ref={ref}
      data-slot="dropdown-item"
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm transition-colors duration-fast outline-none',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        destructive
          ? 'text-danger-fg data-[highlighted]:bg-danger-soft'
          : 'text-fg data-[highlighted]:bg-hover data-[highlighted]:text-fg',
        className,
      )}
      {...rest}
    >
      {leftIcon && <span className="text-fg-subtle [&_svg]:size-4">{leftIcon}</span>}
      <span className="flex-1">{children}</span>
      {shortcut && <span className="text-2xs tracking-wider text-fg-subtle">{shortcut}</span>}
    </RD.Item>
  ),
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

export const DropdownMenuLabel = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof RD.Label>>(
  ({ className, ...rest }, ref) => (
    <RD.Label
      ref={ref}
      data-slot="dropdown-label"
      className={cn('px-2 py-1.5 type-overline text-fg-subtle', className)}
      {...rest}
    />
  ),
);
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RD.Separator>
>(({ className, ...rest }, ref) => (
  <RD.Separator
    ref={ref}
    data-slot="dropdown-separator"
    className={cn('my-1 h-px bg-line', className)}
    {...rest}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

interface DropdownMenuRadioItemProps extends ComponentPropsWithoutRef<typeof RD.RadioItem> {
  leftIcon?: ReactNode;
}

export const DropdownMenuRadioItem = forwardRef<HTMLDivElement, DropdownMenuRadioItemProps>(
  ({ className, leftIcon, children, ...rest }, ref) => (
    <RD.RadioItem
      ref={ref}
      data-slot="dropdown-radio-item"
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-fg transition-colors duration-fast outline-none',
        'data-[highlighted]:bg-hover',
        className,
      )}
      {...rest}
    >
      <span className="flex size-4 items-center justify-center">
        <RD.ItemIndicator>
          <Check size={14} className="text-brand" />
        </RD.ItemIndicator>
      </span>
      {leftIcon && <span className="text-fg-subtle [&_svg]:size-4">{leftIcon}</span>}
      <span className="flex-1">{children}</span>
    </RD.RadioItem>
  ),
);
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';
